import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Typography,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { DotsSixVertical, Trash, PencilSimple, Plus, Check } from '@phosphor-icons/react';
import api from '../../../utils/api';

const BOOKEND_SLUGS = ['enquired', 'completed'];
const EXCLUDED_FROM_POPUP = ['archived'];
/** Slugs seeded by the API; removing them would break the pipeline or be re-created on next load */
const DEFAULT_PIPELINE_SLUGS = new Set([
  'enquired',
  'in_progress',
  'booked',
  'completed',
  'archived'
]);

const stageCannotBeRemoved = (stage) =>
  stage?.isLocked === true || (stage?.slug && DEFAULT_PIPELINE_SLUGS.has(stage.slug));

const eligibleTargetsForRemoval = (stages, removedStage) => {
  if (!removedStage?._id) return [];
  const removedId = String(removedStage._id);
  return stages
    .filter((s) => !s.isArchived && s.slug !== 'archived' && String(s._id) !== removedId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const CRMStagesManager = ({ open, onClose, stages = [], businessId, onStagesUpdated }) => {
  const [localStages, setLocalStages] = useState(stages);
  const [newStageName, setNewStageName] = useState('');
  const [renaming, setRenaming] = useState({});
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [removeDialog, setRemoveDialog] = useState(null);
  const [moveTargetStageId, setMoveTargetStageId] = useState('');

  useEffect(() => {
    setLocalStages(stages);
  }, [stages]);

  useEffect(() => {
    if (open) setActionError('');
  }, [open]);

  const eligibleMoveTargets = useMemo(
    () => eligibleTargetsForRemoval(localStages, removeDialog?.stage),
    [localStages, removeDialog?.stage]
  );

  const { enquired, completed, middleStages } = useMemo(() => {
    const sorted = [...localStages]
      .filter(s => !s.isArchived && !EXCLUDED_FROM_POPUP.includes(s.slug))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const enquired = sorted.find(s => s.slug === 'enquired');
    const completed = sorted.find(s => s.slug === 'completed');
    const middleStages = sorted.filter(s => !BOOKEND_SLUGS.includes(s.slug));
    return { enquired, completed, middleStages };
  }, [localStages]);

  const handleAddStage = async () => {
    if (!newStageName.trim()) return;
    setSaving(true);
    try {
      const response = await api.post('/api/crm/stages', { name: newStageName.trim(), businessId });
      const created = response.data?.stage;
      setLocalStages(prev => [...prev, created]);
      setNewStageName('');
      onStagesUpdated?.();
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (stageId, name) => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const response = await api.put(`/api/crm/stages/${stageId}`, { name: name.trim() });
      const updated = response.data?.stage;
      setLocalStages(prev => prev.map(s => (s._id === stageId ? updated : s)));
      setRenaming({});
      onStagesUpdated?.();
    } finally {
      setSaving(false);
    }
  };

  const closeRemoveDialog = () => {
    setRemoveDialog(null);
    setMoveTargetStageId('');
  };

  const tryRemoveStage = async (stage, moveLeadsToStageId = null) => {
    setSaving(true);
    setActionError('');
    try {
      await api.delete(`/api/crm/stages/${stage._id}`, {
        data: moveLeadsToStageId ? { moveLeadsToStageId } : {}
      });
      setLocalStages((prev) => prev.filter((s) => s._id !== stage._id));
      closeRemoveDialog();
      onStagesUpdated?.();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.code === 'STAGE_HAS_LEADS') {
        const targets = eligibleTargetsForRemoval(localStages, stage);
        const defaultId =
          targets.find((s) => s.slug === 'in_progress')?._id || targets[0]?._id || '';
        setMoveTargetStageId(String(defaultId));
        setRemoveDialog({
          stage,
          step: 'reassign',
          leadCount: err.response.data.leadCount
        });
      } else {
        const message =
          err.response?.data?.message || err.message || 'Could not remove that stage';
        setActionError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (result) => {
    if (!result.destination) return;
    const items = [...middleStages];
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    const stageIds = [
      ...(enquired ? [enquired._id] : []),
      ...items.map(s => s._id),
      ...(completed ? [completed._id] : [])
    ].filter(Boolean);
    try {
      await api.put('/api/crm/stages/reorder', { businessId, stageIds });
      setLocalStages([enquired, ...items, completed].filter(Boolean));
      onStagesUpdated?.();
    } catch (error) {}
  };

  const renderBookendItem = (stage) => (
    <ListItem
      sx={{
        bgcolor: 'grey.200',
        borderRadius: 1,
        mb: 1,
        '&:hover': { bgcolor: 'grey.200' }
      }}
    >
      <ListItemText primary={<Typography variant="subtitle2">{stage.name}</Typography>} />
    </ListItem>
  );

  const renderMiddleItem = (stage, index) => (
    <Draggable key={stage._id} draggableId={stage._id} index={index}>
      {(dragProvided) => (
        <ListItem
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          sx={
            renaming[stage._id] !== undefined
              ? {
                  pr: '112px',
                  '& .MuiListItemSecondaryAction-root': { right: 10 }
                }
              : undefined
          }
          secondaryAction={
            <Stack direction="row" spacing={0.5}>
              <IconButton
                edge="end"
                size="small"
                onClick={() =>
                  renaming[stage._id] !== undefined
                    ? handleRename(stage._id, renaming[stage._id])
                    : setRenaming({ [stage._id]: stage.name })
                }
              >
                <PencilSimple size={18} />
              </IconButton>
              {!stageCannotBeRemoved(stage) && (
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => {
                    setRemoveDialog({ stage, step: 'confirm' });
                    setMoveTargetStageId('');
                    setActionError('');
                  }}
                >
                  <Trash size={18} />
                </IconButton>
              )}
            </Stack>
          }
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
            <span {...dragProvided.dragHandleProps}>
              <DotsSixVertical size={18} />
            </span>
            {renaming[stage._id] !== undefined ? (
              <TextField
                size="small"
                value={renaming[stage._id]}
                onChange={(e) => setRenaming({ [stage._id]: e.target.value })}
                onBlur={() => handleRename(stage._id, renaming[stage._id])}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRename(stage._id, renaming[stage._id]);
                  }
                }}
                autoFocus
                sx={{ flex: 1, minWidth: 0 }}
                InputProps={{
                  sx: {
                    '&.MuiOutlinedInput-root': { pr: '10px' },
                    '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
                      marginLeft: '6px',
                      marginRight: '6px'
                    }
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleRename(stage._id, renaming[stage._id])}
                        disabled={saving || !String(renaming[stage._id] || '').trim()}
                        aria-label="Save stage name"
                      >
                        <Check size={20} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            ) : (
              <ListItemText primary={<Typography variant="subtitle2">{stage.name}</Typography>} />
            )}
          </Stack>
        </ListItem>
      )}
    </Draggable>
  );

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Manage CRM Stages</DialogTitle>
      <DialogContent>
        {actionError ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
            {actionError}
          </Alert>
        ) : null}
        <List>
          {enquired && renderBookendItem(enquired)}
          <DragDropContext onDragEnd={handleReorder}>
            <Droppable droppableId="middle-stages">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {middleStages.map((stage, index) => renderMiddleItem(stage, index))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.5, mb: 1 }}>
            <TextField
              placeholder="Add New Stage"
              size="small"
              value={newStageName}
              onChange={(e) => setNewStageName(e.target.value)}
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddStage();
                }
              }}
              sx={{ flex: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleAddStage}
                      disabled={saving || !newStageName.trim()}
                      edge="end"
                      aria-label="Add stage"
                    >
                      <Plus size={20} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Stack>
          {completed && renderBookendItem(completed)}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={Boolean(removeDialog)}
      onClose={() => {
        if (!saving) closeRemoveDialog();
      }}
      maxWidth="xs"
      fullWidth
      disableScrollLock
    >
      <DialogTitle>Remove stage</DialogTitle>
      <DialogContent>
        {removeDialog?.step === 'confirm' ? (
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            This removes &quot;{removeDialog.stage.name}&quot; from your pipeline. You cannot undo
            this.
          </Typography>
        ) : null}
        {removeDialog?.step === 'reassign' ? (
          <>
            <Alert severity="info" sx={{ mt: 0.5, mb: 2 }}>
              {removeDialog.leadCount === 1
                ? '1 couple is in this stage. Move them to another stage, then remove this one.'
                : `${removeDialog.leadCount} couples are in this stage. Move them to another stage, then remove this one.`}
            </Alert>
            <FormControl fullWidth size="small">
              <InputLabel id="crm-move-couples-stage-label">Move couples to</InputLabel>
              <Select
                labelId="crm-move-couples-stage-label"
                label="Move couples to"
                value={moveTargetStageId}
                onChange={(e) => setMoveTargetStageId(e.target.value)}
              >
                {eligibleMoveTargets.map((s) => (
                  <MenuItem key={s._id} value={String(s._id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeRemoveDialog} disabled={saving}>
          Cancel
        </Button>
        {removeDialog?.step === 'confirm' ? (
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={() => tryRemoveStage(removeDialog.stage)}
          >
            Remove stage
          </Button>
        ) : null}
        {removeDialog?.step === 'reassign' ? (
          <Button
            color="error"
            variant="contained"
            disabled={saving || !moveTargetStageId}
            onClick={() => tryRemoveStage(removeDialog.stage, moveTargetStageId)}
          >
            Move couples and remove stage
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CRMStagesManager;
